import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import CatalogBreadcrumb from './CatalogBreadcrumb';
import HomeExhibitions from './HomeExhibitions';
import HomeNextExhibitionBanner from './HomeNextExhibitionBanner';
import PageSeoHelmet from './PageSeoHelmet';
import { useExhibitions } from './useExhibitions';
import { useSiteSettings } from './SiteSettingsContext';
import { HOME_COPY_DEFAULTS } from './content/pageCopyDefaults';
import { exhibitionsScheduleJsonLd, uniqueCountriesFromExhibitions } from './exhibitionsDisplay';
import { brandNameFromSettings } from './siteBrand';
import './exhibitions-page.css';
import StorePageLoading from './components/StorePageLoading';
import StorefrontEmptyState from './components/StorefrontEmptyState';

/** Full exhibition schedule at /exhibitions — linked from home banner & footer. */
export default function ExhibitionsPage() {
  const exhibitions = useExhibitions();
  const { settings: site } = useSiteSettings();
  const homeCopy = site.page_copy?.home || {};
  const brandName = brandNameFromSettings(site);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const eyebrow = homeCopy.exhibitions_eyebrow || HOME_COPY_DEFAULTS.exhibitions_eyebrow;
  const title = homeCopy.exhibitions_title || HOME_COPY_DEFAULTS.exhibitions_title;
  const subtitle = homeCopy.exhibitions_subtitle || HOME_COPY_DEFAULTS.exhibitions_subtitle;

  const hasShows = exhibitions.upcoming.length + exhibitions.recent.length > 0;
  const countryCount = useMemo(
    () => uniqueCountriesFromExhibitions([...exhibitions.upcoming, ...exhibitions.recent]).length,
    [exhibitions.upcoming, exhibitions.recent],
  );

  const scheduleJsonLd = useMemo(
    () =>
      exhibitions.status === 'ready' && exhibitions.upcoming.length > 0
        ? exhibitionsScheduleJsonLd(exhibitions.upcoming, { brandName })
        : null,
    [exhibitions.status, exhibitions.upcoming, brandName],
  );

  const whatsappHref = site.whatsapp_href || 'https://wa.me/919680384332';

  return (
    <div className="exhibitions-page store-page store-page-enter">
      <PageSeoHelmet page="exhibitions">
        {scheduleJsonLd ? <script type="application/ld+json">{scheduleJsonLd}</script> : null}
      </PageSeoHelmet>

      <div className="container store-page__container py-4 py-lg-5">
        <section className="exhibitions-hero shop-hero" aria-labelledby="exhibitions-page-title">
          <CatalogBreadcrumb
            align="start"
            className="exhibitions-page-breadcrumb catalog-breadcrumb--refined mb-3"
            items={[{ to: '/', label: 'Home' }, { label: 'Exhibitions' }]}
          />

          <div className="shop-hero-panel exhibitions-hero-panel text-center">
            {eyebrow ? <p className="shop-hero-eyebrow">{eyebrow}</p> : null}
            <h1 id="exhibitions-page-title" className="shop-page-title mb-2">
              {title}
            </h1>
            {subtitle ? (
              <p className="shop-page-subtitle shop-page-subtitle--editorial mb-0">{subtitle}</p>
            ) : null}
            {exhibitions.status === 'ready' && hasShows ? (
              <div className="exhibitions-page-hero-meta">
                {exhibitions.upcoming.length > 0 ? (
                  <span className="exhibitions-stat-pill">
                    {exhibitions.upcoming.length}{' '}
                    {exhibitions.upcoming.length === 1 ? 'upcoming show' : 'upcoming shows'}
                  </span>
                ) : null}
                {countryCount > 0 ? (
                  <span className="exhibitions-stat-pill exhibitions-stat-pill--muted">
                    {countryCount} {countryCount === 1 ? 'country' : 'countries'}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          {exhibitions.status === 'ready' && hasShows ? (
            <nav className="exhibitions-page-tabs" aria-label="Schedule sections">
              {exhibitions.upcoming.length > 0 ? (
                <a href="#exhibitions-upcoming" className="exhibitions-page-tabs__link">
                  Upcoming
                </a>
              ) : null}
              {exhibitions.recent.length > 0 ? (
                <a href="#exhibitions-past" className="exhibitions-page-tabs__link">
                  Past
                </a>
              ) : null}
            </nav>
          ) : null}
        </section>

        {exhibitions.status === 'loading' ? (
          <StorePageLoading variant="exhibitions" message="Loading exhibition schedule…" />
        ) : null}

        {exhibitions.status === 'ready' && exhibitions.upcoming.length > 0 ? (
          <section className="exhibitions-page-featured" aria-label="Next show">
            <HomeNextExhibitionBanner
              upcoming={exhibitions.upcoming}
              homeCopy={homeCopy}
              ctaTo={
                exhibitions.upcoming[0]?.external_url
                  ? String(exhibitions.upcoming[0].external_url)
                  : '#exhibitions-upcoming'
              }
              ctaLabel={
                exhibitions.upcoming[0]?.external_url ? 'Event details' : 'View full schedule'
              }
            />
          </section>
        ) : null}

        {exhibitions.status === 'ready' && hasShows ? (
          <div className="exhibitions-page__schedule">
            <HomeExhibitions
              upcoming={exhibitions.upcoming}
              recent={exhibitions.recent}
              status={exhibitions.status}
              showHeader={false}
              pageMode
            />
          </div>
        ) : null}

        {exhibitions.status === 'ready' && !hasShows ? (
          <StorefrontEmptyState
            className="exhibitions-page-empty"
            title="No shows scheduled right now"
            text="We will post new dates here as soon as they are confirmed. You can still shop the full collection online."
          >
            <Link to="/shop" className="btn btn-primary store-cta rounded-pill">
              Browse the shop
            </Link>
            <Link to="/contact" className="btn btn-outline-secondary rounded-pill">
              Contact us
            </Link>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-secondary rounded-pill"
            >
              WhatsApp us
            </a>
          </StorefrontEmptyState>
        ) : null}

        {exhibitions.status === 'ready' && hasShows ? (
          <section className="exhibitions-page-cta utility-card" aria-labelledby="exhibitions-page-cta-title">
            <h2 id="exhibitions-page-cta-title" className="utility-card__title text-center">
              Can&apos;t make it to a show?
            </h2>
            <p className="exhibitions-page-cta__text text-center mx-auto mb-0">
              Shop the full collection online with delivery across India, or message us for custom orders and bulk gifting.
            </p>
            <div className="exhibitions-page-cta__actions">
              <Link to="/shop" className="btn btn-primary store-cta rounded-pill">
                Shop online
              </Link>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary rounded-pill"
              >
                WhatsApp us
              </a>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
